import { useTranslation } from 'react-i18next';
import type { Client, ItemPacking, ItemType, VehicleTypes } from '@hero/schema';
import { useEditClientSettingsManager } from '@hero/web-hooks';
import type { PodCopySetting } from '@hero/web-hooks';
import { ModalForm } from '../../../app/Modal';
import { Input } from '../../../ui/Input/Input';
import { SelectAuto, type SelectAutoOption } from '../../../app/Select/SelectAuto';
import { PriceEdit } from '../../../app/Price/PriceEdit';
import { OrderItemTypeSelect } from '../../Order/Item/OrderItemTypeSelect';
import { OrderPackingTypeSelect } from '../../Order/Item/OrderPackingTypeSelect';
import { VehicleTypeMultiSelector } from '../../Vehicle/VehicleTypeMultiSelector';

export interface EditClientSettingsModalProps {
  client: Client | undefined;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Per-section modal for editing `Client.settings` order defaults.
 *
 * Scope: scalar defaults (item name, type, goods value, weight),
 * vehicle preferences, and the per-client document-copy preference
 * (`pod_settings`) that drives BOTH order POD copies AND
 * original-invoice copies for this client.
 */
export function EditClientSettingsModal({
  client,
  isOpen,
  onClose,
}: EditClientSettingsModalProps) {
  const { t } = useTranslation(['m-client']);
  const { draft, setField, setVehicleTypes, onSave, isPending, isSuccess, reset } =
    useEditClientSettingsManager(client);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSave = async () => {
    try {
      await onSave();
    } catch (err) {
      console.error('Failed to update client settings:', err);
    }
  };

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={handleClose}
      title={t('edit_modal.settings.title')}
      description={t('edit_modal.settings.description')}
      onSave={handleSave}
      isLoading={isPending}
      isSuccess={isSuccess}
      size="md">
      <div className="space-y-4">
        {/* Vehicle types — first because the popover is the heaviest
            interaction in this modal and putting it at the top means
            the user picks vehicles before the form scrolls. We pass
            `disablePortal` so the picker mounts inline (no Radix
            portal): a portaled popover ends up rendered on `<body>`
            outside the modal's scroll container, which makes it look
            like the popover is "floating outside the dialog" and
            stops it from scrolling with the form on smaller viewports. */}
        <FieldShell label={t('edit_modal.field.vehicle_preferred_types.title')}>
          <VehicleTypeMultiSelector
            value={draft.vehicle_preferred_types}
            onChange={(next: VehicleTypes[]) => setVehicleTypes(next)}
            placeholder={t(
              'edit_modal.field.vehicle_preferred_types.placeholder',
            )}
            disablePortal
          />
        </FieldShell>
        <FieldShell label={t('edit_modal.field.item_default_name.title')}>
          <Input
            value={draft.item_default_name}
            placeholder={t('edit_modal.field.item_default_name.placeholder')}
            onChange={e => setField('item_default_name', e.target.value)}
          />
        </FieldShell>
        <FieldShell label={t('edit_modal.field.item_default_type.title')}>
          <OrderItemTypeSelect
            itemType={draft.item_default_type}
            onMaterialChange={(value: ItemType) => setField('item_default_type', value)}
          />
        </FieldShell>
        <FieldShell label={t('edit_modal.field.item_default_cost.title')}>
          <PriceEdit
            value={draft.item_default_cost}
            onChange={value => setField('item_default_cost', value)}
            currency="SAR"
          />
        </FieldShell>
        <FieldShell label={t('edit_modal.field.item_default_weight.title')}>
          <Input
            type="number"
            min={0}
            step={0.1}
            value={draft.item_default_weight}
            placeholder={t('edit_modal.field.item_default_weight.placeholder')}
            onChange={e => setField('item_default_weight', e.target.value)}
          />
        </FieldShell>
        <FieldShell label={t('edit_modal.field.item_default_packing.title')}>
          <OrderPackingTypeSelect
            packing={draft.item_default_packing}
            counter={draft.item_default_quantity}
            showCounter
            onPackingChange={(value: ItemPacking) =>
              setField('item_default_packing', value)
            }
            onCounterChange={value => setField('item_default_quantity', value)}
          />
        </FieldShell>
        {/*
         * Per-client "document copy returns" preference. Drives BOTH
         * the order POD copy AND the original-invoice copy on every
         * new order/invoice for this client — they share the same
         * enum (`'pod_image' | 'return_original_pod'`). The helper
         * copy makes that dual-scope explicit so the user knows what
         * they're toggling.
         */}
        <FieldShell
          label={t('edit_modal.field.pod_settings.title', {
            defaultValue: 'Document copy returns',
          })}>
          <SelectAuto
            options={
              [
                {
                  value: 'pod_image',
                  label: t('edit_modal.field.pod_settings.option_pod_image', {
                    defaultValue: 'Image only (no paper)',
                  }),
                },
                {
                  value: 'return_original_pod',
                  label: t('edit_modal.field.pod_settings.option_return_original_pod', {
                    defaultValue: 'Return paper original',
                  }),
                },
              ] satisfies SelectAutoOption[]
            }
            value={draft.pod_settings ?? ''}
            onChange={value =>
              setField(
                'pod_settings',
                (value || undefined) as PodCopySetting | undefined,
              )
            }
            placeholder={t('edit_modal.field.pod_settings.placeholder', {
              defaultValue: 'Inherit organisation default',
            })}
            helperText={t('edit_modal.field.pod_settings.description', {
              defaultValue:
                'Applies to both POD copies on orders and original invoice copies. New orders and invoices for this client will use this default.',
            })}
          />
        </FieldShell>
      </div>
    </ModalForm>
  );
}

function FieldShell({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {children}
    </div>
  );
}
